import { useMemo } from 'react';

import { ValidationContextName } from '@interfaces/connector';

import { VALIDATION_WARNING_GROUP_DESCRIPTIONS } from '@components/settings/v2/structure/connector/productionConnectorSetup/components/ValidationErrorsPopup/constants';
import { parseValidationWarningContext } from '@components/settings/v2/structure/connector/productionConnectorSetup/components/ValidationErrorsPopup/helpers/parseValidationWarningContext';
import { stripFolderPathFromMessage } from '@components/settings/v2/structure/connector/productionConnectorSetup/components/ValidationErrorsPopup/helpers/stripFolderPathFromMessage';
import { ParsedValidationWarning, ValidationErrorsPopupProps, ValidationWarningsGroup } from '@components/settings/v2/structure/connector/productionConnectorSetup/components/ValidationErrorsPopup/types';

type UseManageValidationErrorsPopupParams = Pick<ValidationErrorsPopupProps, 'validationWarnings'>;

export const useManageValidationErrorsPopup = ({ validationWarnings }: UseManageValidationErrorsPopupParams) => {
  const groupedWarnings = useMemo<ValidationWarningsGroup[]>(() => {
    if (!validationWarnings?.length) return [];

    const groupsByName = new Map<ValidationContextName, ParsedValidationWarning[]>();

    validationWarnings.forEach((warning) => {
      const context = parseValidationWarningContext({
        validationContextName: warning.validationContextName,
        validationContextValue: warning.validationContextValue,
      });
      const parsed: ParsedValidationWarning = {
        ...warning,
        ...context,
        message: stripFolderPathFromMessage(warning.message, context.folderPath),
      };

      const existing = groupsByName.get(warning.validationContextName);
      if (existing) existing.push(parsed);
      else groupsByName.set(warning.validationContextName, [parsed]);
    });

    return Array.from(groupsByName.entries()).map(([validationContextName, warnings]) => ({
      validationContextName,
      description: VALIDATION_WARNING_GROUP_DESCRIPTIONS[validationContextName],
      warnings,
    }));
  }, [validationWarnings]);

  return { groupedWarnings };
};
